// hooks/useTreatmentPlanFilter.ts
import {useMemo, useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import {safeParseInt} from 'utils/ConstFunctions'
import useAllUserPlan from '@hooks/useAllUserPlan'

export const useTreatmentPlanFilter = () => {
  const location = useLocation()
  const {isStarterPlanUser, isEnterprisePlanUser, isGrowthPlanUser} = useAllUserPlan()
  const {allTreatmentPlanList} = useSelector(
    (state: RootState) => state.leadsProfileTreatmentPlanReducer
  )
  const {data} = useSelector((state: RootState) => state.apiGetLeadsProfileDetails)
  const searchParams = new URLSearchParams(location.search)
  const urlPlanId = searchParams.get('treatment_plan_id') ?? searchParams.get('treatmentPlanId')
  const taskIdParam = searchParams.get('task_id') ?? searchParams.get('taskId')
  const taskIdFromUrl = safeParseInt(taskIdParam)
  const hasGrowthRefinementTask =
    isGrowthPlanUser && searchParams.get('refinement') === 'true' && taskIdFromUrl > 0
  const isGrowthRefinementDraft = hasGrowthRefinementTask && !urlPlanId

  // Get the current patient ID from Redux
  const currentPatientId = data?.patient_details?.id

  // Use a ref to track the previous patient ID
  const [previousPatientId, setPreviousPatientId] = useState<number | undefined>(undefined)
  // Use state to track if the plan list has been reset
  const [isPlanListReset, setIsPlanListReset] = useState(false)

  // Reset plan list when patient changes
  useEffect(() => {
    if (currentPatientId && previousPatientId !== currentPatientId) {
      setPreviousPatientId(currentPatientId)
      setIsPlanListReset(true)

      // Reset after a short delay to allow new data to load
      setTimeout(() => {
        setIsPlanListReset(false)
      }, 300)
    }
  }, [currentPatientId, previousPatientId])

  const getPlanIdFromListItem = useCallback((plan: any) => {
    const id =
      plan?.treatment_plan_id ??
      plan?.treatmentPlanId ??
      plan?.aligner_treatment_id ??
      plan?.alignerTreatmentId ??
      plan?.braces_treatment_id ??
      plan?.bracesTreatmentId ??
      plan?.plan_id ??
      plan?.id ??
      null

    const parsedId = safeParseInt(id)
    return parsedId > 0 ? parsedId : null
  }, [])

  // Get active plan list - prefer ACTIVE plans, but keep other created plans in play for refinement flows
  const activePlanList = useMemo(() => {
    // If the plan list has been reset, return empty array
    if (isPlanListReset) {
      return []
    }

    return (allTreatmentPlanList ?? [])
      .filter((plan: any) => plan.treatment_status === 'ACTIVE')
      .map((plan: any) => {
        return getPlanIdFromListItem(plan)
      })
      .filter((id: number | null) => id !== null)
      .filter((id: number, idx: number, arr: number[]) => arr.indexOf(id) === idx)
      .sort((a: number, b: number) => a - b)
  }, [allTreatmentPlanList, getPlanIdFromListItem, isPlanListReset])

  // Get all plan IDs (both active and inactive) - for displaying all plans
  const allPlanIds = useMemo(() => {
    // If the plan list has been reset, return empty array
    if (isPlanListReset) {
      return []
    }

    return (allTreatmentPlanList ?? [])
      .map((plan: any) => {
        return getPlanIdFromListItem(plan)
      })
      .filter((id: number | null) => id !== null)
      .filter((id: number, idx: number, arr: number[]) => arr.indexOf(id) === idx)
      .sort((a: number, b: number) => a - b)
  }, [allTreatmentPlanList, getPlanIdFromListItem, isPlanListReset])

  const latestPlanId = useMemo(() => {
    // If the plan list has been reset, return null
    if (isPlanListReset) {
      return null
    }
    const planIds = allPlanIds.length > 0 ? allPlanIds : activePlanList
    return planIds.length > 0 ? planIds[planIds.length - 1] : null
  }, [activePlanList, allPlanIds, isPlanListReset])

  const currentPlanId = useMemo(() => {
    // If the plan list has been reset, return null
    if (isPlanListReset || isGrowthRefinementDraft) {
      return null
    }
    return urlPlanId ? safeParseInt(urlPlanId) : latestPlanId
  }, [urlPlanId, latestPlanId, isPlanListReset, isGrowthRefinementDraft])

  const isLatestPlan = useMemo(() => {
    if (isGrowthRefinementDraft) return true
    if (!isStarterPlanUser && !isEnterprisePlanUser && !isGrowthPlanUser) return true
    if (!currentPlanId || !latestPlanId) return true
    return currentPlanId === latestPlanId
  }, [
    isGrowthRefinementDraft,
    isStarterPlanUser,
    isEnterprisePlanUser,
    isGrowthPlanUser,
    currentPlanId,
    latestPlanId,
  ])

  // Refinement-style flows keep older treatment plans read-only.
  const isPastPlan = useMemo(() => {
    if (isGrowthRefinementDraft) return false
    if (hasGrowthRefinementTask && urlPlanId) return true

    // Check if user qualifies for past plan evaluation
    const isQualifiedUser =
      isStarterPlanUser ||
      isEnterprisePlanUser ||
      isGrowthPlanUser ||
      data?.patient_details?.patient_type === 'EXISTING_PATIENT'

    if (!isQualifiedUser) return false

    // Need both URL plan ID and latest plan ID to compare
    if (!urlPlanId || !latestPlanId) return false

    const planId = safeParseInt(urlPlanId)

    // Current plan is the latest plan - not a past plan
    if (planId === latestPlanId) return false

    if (isGrowthPlanUser || isEnterprisePlanUser) {
      return true
    }

    // Check the plan status from allTreatmentPlanList
    const planData = allTreatmentPlanList?.find((plan: any) => {
      return getPlanIdFromListItem(plan) === planId
    })

    if (planData) {
      return planData.treatment_status === 'DEACTIVATED' || planData.treatment_status === 'COMPLETE'
    }

    return true
  }, [
    isGrowthRefinementDraft,
    hasGrowthRefinementTask,
    isStarterPlanUser,
    isEnterprisePlanUser,
    isGrowthPlanUser,
    data?.patient_details?.patient_type,
    urlPlanId,
    latestPlanId,
    allTreatmentPlanList,
    getPlanIdFromListItem,
  ])

  // Method to return the latest plan ID
  const getLatestPlanId = useCallback(() => {
    // If plan list is reset, return null
    if (isPlanListReset) {
      return null
    }
    return latestPlanId
  }, [latestPlanId, isPlanListReset])

  // Method to get plan data by ID
  const getPlanDataById = useCallback(
    (planId: number) => {
      if (!allTreatmentPlanList || !planId) return null

      return (
        allTreatmentPlanList.find((plan: any) => {
          return getPlanIdFromListItem(plan) === planId
        }) || null
      )
    },
    [allTreatmentPlanList, getPlanIdFromListItem]
  )

  // Method to get treatment status by plan ID
  const getTreatmentStatusByPlanId = useCallback(
    (planId: number) => {
      const planData: any = getPlanDataById(planId)
      return planData?.treatment_plan_status || null
    },
    [getPlanDataById]
  )

  // Method to get aligner journey ID by plan ID
  const getAlignerJourneyIdByPlanId = useCallback(
    (planId: number) => {
      const planData: any = getPlanDataById(planId)
      if (!planData) return null

      // Check if aligner_journey_id exists directly
      if (planData.aligner_journey_id) {
        return planData.aligner_journey_id
      }

      // Check if aligner_journeys array exists and has items
      if (planData.aligner_journeys && planData.aligner_journeys.length > 0) {
        return planData.aligner_journeys[0]?.id || null
      }

      return null
    },
    [getPlanDataById]
  )

  // Check if a plan is active
  const isPlanActive = useCallback(
    (planId: number) => {
      const planData = getPlanDataById(planId)
      if (!planData) return false
      return planData.treatment_status === 'ACTIVE'
    },
    [getPlanDataById]
  )

  // Get the latest plan ID for a specific patient
  const getLatestPlanIdForPatient = useCallback(
    (patientPlanList: any[]) => {
      if (!patientPlanList || patientPlanList.length === 0) return null

      // Prefer active plans, but fall back to any created plan for draft/approval/refinement states.
      const activePlans = patientPlanList.filter((plan: any) => plan.treatment_status === 'ACTIVE')
      const plansToUse = activePlans.length > 0 ? activePlans : patientPlanList

      // Get aligner treatment IDs
      const planIds = plansToUse
        .map((plan: any) => {
          return getPlanIdFromListItem(plan)
        })
        .filter((id: number | null) => id !== null)
        .sort((a: number, b: number) => a - b)

      return planIds.length > 0 ? planIds[planIds.length - 1] : null
    },
    [getPlanIdFromListItem]
  )

  // Reset function to clear the plan list
  const resetPlanList = useCallback(() => {
    setIsPlanListReset(true)
    setTimeout(() => {
      setIsPlanListReset(false)
    }, 300)
  }, [])

  return {
    isStarterPlanUser,
    currentPlanId,
    latestPlanId,
    isLatestPlan,
    isPastPlan,
    urlPlanId,
    activePlanList,
    allPlanIds,
    hasGrowthRefinementTask,
    isGrowthRefinementDraft,
    taskIdFromUrl,
    getLatestPlanId,
    getPlanDataById,
    getTreatmentStatusByPlanId,
    getAlignerJourneyIdByPlanId,
    isPlanActive,
    getLatestPlanIdForPatient,
    resetPlanList,
    isPlanListReset,
  }
}
